// maplibre-gl is a CommonJS package. Importing its members by NAME
// (`import { Map, Popup } from 'maplibre-gl'`) is an unresolvable named import
// from CJS under strict-ESM bundlers like Turbopack — it fails at link time and
// makes this whole module resolve to an empty namespace (every export lost, map
// charts blank). Use the default import (the CJS module.exports object) for
// runtime values, and a type-only import for the types.
import maplibregl from 'maplibre-gl';
import type { Map as MLMap, GeoJSONSource, LngLatBoundsLike } from 'maplibre-gl';
import simplify from 'simplify-js';

const { Map: MapLibreMap, Popup } = maplibregl;
import { light, dark } from './themes';
import { MapChartOptions, BORDER_COLORS, BACKGROUND_COLORS } from './types';

function clearMap(chart: any) {
    const map: MLMap = chart.map;
    if (!map) return;
    (chart._mapLayerIds  as string[] || []).forEach(id => {
        try { if (map.getLayer(id))   map.removeLayer(id);  } catch (_) { /* already gone */ }
    });
    (chart._mapSourceIds as string[] || []).forEach(id => {
        try { if (map.getSource(id))  map.removeSource(id); } catch (_) { /* already gone */ }
    });
    chart._mapLayerIds  = [];
    chart._mapSourceIds = [];
}

function renderDatasets(chart: any) {
    const map: MLMap = chart.map;
    if (!map || !map.isStyleLoaded()) return;

    clearMap(chart);

    let northeast = { lat: -90,  lng: -180 };
    let southwest = { lat:  90,  lng:  180 };
    let hasData   = false;

    const bump = (lat: number, lng: number) => {
        northeast.lat = Math.max(northeast.lat, lat);
        northeast.lng = Math.max(northeast.lng, lng);
        southwest.lat = Math.min(southwest.lat, lat);
        southwest.lng = Math.min(southwest.lng, lng);
        hasData = true;
    };

    const layerIds:  string[] = [];
    const sourceIds: string[] = [];

    const hidden: boolean[] = chart._metasets.map((a: any) => a.hidden);
    chart.data.datasets
        .filter((_: any, i: number) => !hidden[i])
        .forEach((dataset: any, idx: number) => {
            const bg:     string = dataset.backgroundColor || BACKGROUND_COLORS[idx % BACKGROUND_COLORS.length];
            const border: string = dataset.borderColor     || BORDER_COLORS[idx % BORDER_COLORS.length];
            const lw:     number = dataset.borderWidth || 2;

            // ── MARKERS ────────────────────────────────────────────────────────
            if (dataset.type === 'marker') {
                const pts: [number, number][] = Array.isArray(dataset.data[0])
                    ? dataset.data : [dataset.data];

                pts.forEach(([lat, lng]: [number, number]) => bump(lat, lng));

                const src  = `cjm-mk-${idx}`;
                const clId = `cjm-mk-cl-${idx}`;
                const ccId = `cjm-mk-cc-${idx}`;
                const ptId = `cjm-mk-pt-${idx}`;

                map.addSource(src, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: pts.map(([lat, lng]: [number, number]) => ({
                            type: 'Feature' as const,
                            properties: { label: dataset.label ?? '' },
                            geometry: { type: 'Point' as const, coordinates: [lng, lat] }
                        }))
                    },
                    cluster: true,
                    clusterMaxZoom: 12,
                    clusterRadius: 50
                });

                // cluster bubble
                map.addLayer({
                    id: clId, type: 'circle', source: src,
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': border,
                        'circle-opacity': 0.85,
                        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 24, 50, 32]
                    }
                });

                // cluster count label — wrapped so a missing font doesn't kill the rest
                try {
                    map.addLayer({
                        id: ccId, type: 'symbol', source: src,
                        filter: ['has', 'point_count'],
                        layout: {
                            'text-field': '{point_count_abbreviated}',
                            'text-font': ['Noto Sans Bold', 'Noto Sans Regular'],
                            'text-size': 14,
                            'text-anchor': 'center',
                            // Nudge up by 0.15em — text-anchor centers the full
                            // bounding box (cap + descender space), so glyphs
                            // without descenders sit visually below center.
                            'text-offset': [0, -0.15]
                        },
                        paint: {
                            'text-color': '#ffffff',
                            'text-halo-color': 'rgba(0,0,0,0.25)',
                            'text-halo-width': 1
                        }
                    });
                    layerIds.push(ccId);
                } catch (_) { /* font unavailable — circles still show */ }

                // individual unclustered point
                map.addLayer({
                    id: ptId, type: 'circle', source: src,
                    filter: ['!', ['has', 'point_count']],
                    paint: {
                        'circle-color': bg,
                        'circle-radius': 6,
                        'circle-stroke-width': 2,
                        'circle-stroke-color': border
                    }
                });

                map.on('click', ptId, e => {
                    const label = (e.features?.[0]?.properties?.label as string) ?? '';
                    new Popup().setLngLat(e.lngLat).setHTML(`<h3>${label}</h3>`).addTo(map);
                });

                map.on('click', clId, async e => {
                    const feats = map.queryRenderedFeatures(e.point, { layers: [clId] });
                    if (!feats.length) return;
                    const cid   = feats[0].properties?.cluster_id as number;
                    const gsrc  = map.getSource(src) as GeoJSONSource;
                    const zoom  = await gsrc.getClusterExpansionZoom(cid);
                    const coord = (feats[0].geometry as GeoJSON.Point).coordinates as [number, number];
                    map.easeTo({ center: coord, zoom });
                });

                map.on('mouseenter', clId, () => { map.getCanvas().style.cursor = 'pointer'; });
                map.on('mouseleave', clId, () => { map.getCanvas().style.cursor = ''; });
                map.on('mouseenter', ptId, () => { map.getCanvas().style.cursor = 'pointer'; });
                map.on('mouseleave', ptId, () => { map.getCanvas().style.cursor = ''; });

                layerIds.push(clId, ptId);
                sourceIds.push(src);

            // ── POLYLINES ──────────────────────────────────────────────────────
            } else if (dataset.type === 'polyline') {
                let pts: [number, number][] = dataset.data;
                if (pts.length > 100) {
                    const s = simplify(pts.map(([x, y]: [number, number]) => ({ x, y })), 0.1);
                    pts = s.map((p: { x: number; y: number }) => [p.x, p.y]);
                }
                pts.forEach(([lat, lng]: [number, number]) => bump(lat, lng));

                const src = `cjm-pl-${idx}`;
                const lid = `cjm-pl-l-${idx}`;

                map.addSource(src, {
                    type: 'geojson',
                    data: {
                        type: 'Feature', properties: {},
                        geometry: { type: 'LineString',
                            coordinates: pts.map(([lat, lng]: [number, number]) => [lng, lat]) }
                    }
                });
                map.addLayer({
                    id: lid, type: 'line', source: src,
                    layout: { 'line-cap': 'round', 'line-join': 'round' },
                    paint: { 'line-color': border, 'line-width': lw }
                });

                const cLat = pts.reduce((s, [la]: [number, number]) => s + la, 0) / pts.length;
                const cLng = pts.reduce((s, [, lo]: [number, number]) => s + lo, 0) / pts.length;
                map.on('click', lid, () => {
                    new Popup().setLngLat([cLng, cLat])
                        .setHTML(`<h3>${dataset.label ?? ''}</h3>`).addTo(map);
                });
                map.on('mouseenter', lid, () => { map.getCanvas().style.cursor = 'pointer'; });
                map.on('mouseleave', lid, () => { map.getCanvas().style.cursor = ''; });

                layerIds.push(lid);
                sourceIds.push(src);

            // ── POLYGONS ───────────────────────────────────────────────────────
            } else if (dataset.type === 'polygon') {
                const pts: [number, number][] = dataset.data;
                pts.forEach(([lat, lng]: [number, number]) => bump(lat, lng));

                const coords = pts.map(([lat, lng]: [number, number]) => [lng, lat]);
                if (coords.length > 0) {
                    const f = coords[0], l = coords[coords.length - 1];
                    if (f[0] !== l[0] || f[1] !== l[1]) coords.push([f[0], f[1]]);
                }

                const src = `cjm-pg-${idx}`;
                const fid = `cjm-pg-f-${idx}`;
                const oid = `cjm-pg-o-${idx}`;

                map.addSource(src, {
                    type: 'geojson',
                    data: { type: 'Feature', properties: {},
                        geometry: { type: 'Polygon', coordinates: [coords] } }
                });
                map.addLayer({ id: fid, type: 'fill', source: src,
                    paint: { 'fill-color': bg, 'fill-opacity': 0.35 } });
                map.addLayer({ id: oid, type: 'line', source: src,
                    paint: { 'line-color': border, 'line-width': lw, 'line-opacity': 0.8 } });

                map.on('click', fid, e => {
                    new Popup().setLngLat(e.lngLat)
                        .setHTML(`<h3>${dataset.label ?? ''}</h3>`).addTo(map);
                });
                map.on('mouseenter', fid, () => { map.getCanvas().style.cursor = 'pointer'; });
                map.on('mouseleave', fid, () => { map.getCanvas().style.cursor = ''; });

                layerIds.push(fid, oid);
                sourceIds.push(src);
            }
        });

    chart._mapLayerIds  = layerIds;
    chart._mapSourceIds = sourceIds;

    // Only fitBounds once the container has real dimensions
    const container = map.getContainer();
    if (hasData && container.clientWidth > 0 && container.clientHeight > 0) {
        const bounds: LngLatBoundsLike = [
            [southwest.lng, southwest.lat],
            [northeast.lng, northeast.lat]
        ];
        map.fitBounds(bounds, { padding: 60, maxZoom: 10, animate: false });
    }
}

function scheduleRender(chart: any) {
    const map: MLMap = chart.map;
    if (!map) return;
    if (map.isStyleLoaded()) {
        renderDatasets(chart);
    } else {
        map.once('load', () => renderDatasets(chart));
    }
}

export const MapPlugin = {
    id: 'map',

    beforeInit: (chart: any) => {
        if (typeof window === 'undefined') return;
        const parent: HTMLElement | null = chart.canvas.parentElement;
        if (!parent) return;

        const options = chart.options as MapChartOptions;
        parent.style.position = 'relative';

        // Remove any stale map container left on this parent by a previous chart
        // instance. react-chartjs-2 recreates the Chart on resize / config change,
        // which runs beforeInit again; without this cleanup the old MapLibre map
        // stays in the DOM and two maps overlap (visible after a window resize).
        parent.querySelectorAll(':scope > [data-cjm-map]').forEach((el) => el.remove());

        const container = document.createElement('div');
        container.setAttribute('data-cjm-map', '');
        // z-index:2 keeps map above the Chart.js canvas (which uses z-index:1).
        // No overflow:hidden — MapLibre appends popups as children of this div,
        // and clipping them makes popups invisible.
        container.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border-radius:8px;z-index:2;';
        parent.appendChild(container);

        const map = new MapLibreMap({
            container,
            style: options.theme === 'dark' ? dark : light,
            center: options.center ? [options.center.lng, options.center.lat] : [0, 0],
            zoom: options.zoom ?? 2,
        });

        chart.map          = map;
        chart._mapLayerIds  = [];
        chart._mapSourceIds = [];
        chart._mapSized     = false;   // becomes true after first beforeDraw sizes the container
    },

    beforeDraw: (chart: any) => {
        const map: MLMap = chart.map;
        if (!map || !chart.chartArea) return;

        const { top, left, width, height } = chart.chartArea;
        const container = map.getContainer();
        container.style.top    = `${top}px`;
        container.style.left   = `${left}px`;
        container.style.width  = `${width}px`;
        container.style.height = `${height}px`;
        map.resize();

        // First time the container gets real dimensions: trigger a fresh render
        // so fitBounds runs against an actual pixel size.
        if (!chart._mapSized && width > 0 && height > 0) {
            chart._mapSized = true;
            scheduleRender(chart);
        }
    },

    afterUpdate: (chart: any) => {
        // Only call scheduleRender if the container is already sized;
        // otherwise beforeDraw will pick it up on first paint.
        if (chart._mapSized) scheduleRender(chart);
    },

    destroy: (chart: any) => {
        clearMap(chart);
        if (chart.map) {
            const container = (chart.map as MLMap).getContainer();
            (chart.map as MLMap).remove();
            // MapLibre's remove() tears down the map but leaves the container div
            // we appended in beforeInit — remove it so it can't linger/overlap.
            container?.remove();
            chart.map = null;
        }
    }
};
